import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { isCapacitorNativeSync } from '../native/bridge';
import { readCookie } from '../shared/cookies';

export type OperatingSystem = 'ios' | 'android' | 'web';

interface CapacitorLike {
  getPlatform?: () => string;
}

export interface PlatformDetectionInput {
  capacitor?: CapacitorLike | null;
  cookie?: string;
  userAgent?: string;
}

export interface PlatformState {
  /** RENDER-Betriebssystem (Styling/data-os) — im mobilen Web ebenfalls 'ios'/'android'. */
  os: OperatingSystem;
  /** Echte App-Huelle (Capacitor). NIE aus `os` ableiten, siehe createPlatformState. */
  isNative: boolean;
}

const PlatformContext = createContext<PlatformState | null>(null);

function normalizePlatform(value: string | null | undefined): OperatingSystem | null {
  if (value === 'ios' || value === 'android' || value === 'web') return value;
  return null;
}

export function detectPlatform(input: PlatformDetectionInput = {}): OperatingSystem {
  const win = typeof window !== 'undefined' ? window : undefined;
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const capacitor = input.capacitor ?? (win as (Window & { Capacitor?: CapacitorLike }) | undefined)?.Capacitor;

  // Nur ein 'ios'/'android' von Capacitor ist ein Signal. Sein 'web' ist bloss
  // der Default-Rueckgabewert im Browser und darf den UA-Zweig NICHT
  // ueberstimmen — sonst laege im iPhone-Safari data-os='web' an und der
  // iOS-Zoom-Schutz (`[data-os='ios'] input { font-size:16px }`, base.css)
  // griffe im mobilen Web nicht. Ausserdem haengt die Web-Erkennung dann nicht
  // mehr daran, dass @capacitor/core (PURE-annotiert, also tree-shake-bar)
  // ueberhaupt evaluiert wurde.
  if (capacitor && typeof capacitor.getPlatform === 'function') {
    const fromCapacitor = normalizePlatform(capacitor.getPlatform());
    if (fromCapacitor === 'ios' || fromCapacitor === 'android') return fromCapacitor;
  }

  const cookieSource = input.cookie ?? (typeof document !== 'undefined' ? document.cookie : '');
  const fromCookie = normalizePlatform(readCookie('lorath_native', cookieSource));
  if (fromCookie) return fromCookie;

  const userAgent = input.userAgent ?? nav?.userAgent ?? '';
  if (/iPad|iPhone|iPod/i.test(userAgent)) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return 'web';
}

/**
 * Schreibt die Plattform-Attribute ans <html>.
 *
 * `data-os` ist REINES OS-Styling und liegt auch im mobilen Web an (Safari auf
 * dem iPhone bekommt 'ios'). Das einzige korrekte CSS-Gate fuer „echte App" ist
 * ab jetzt `data-native` — wer nativ-only stylen will, MUSS darauf selektieren,
 * nicht auf data-os.
 */
export function applyPlatformAttribute(os: OperatingSystem, isNative: boolean = isCapacitorNativeSync()): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.os = os;
  if (isNative) root.dataset.native = 'true';
  else delete root.dataset.native;
}

// isNative kommt AUSSCHLIESSLICH von Capacitor (window.Capacitor.isNativePlatform()).
// Frueher wurde es aus `os` abgeleitet — mit der UA-Aufloesung oben waere damit
// jeder mobile Browser „nativ" und bekaeme kaputte Store-Kaufbuttons.
export function createPlatformState(os: OperatingSystem, isNative: boolean = isCapacitorNativeSync()): PlatformState {
  return { os, isNative };
}

export function PlatformProvider({ children }: { children: ReactNode }) {
  const value = useMemo(() => {
    const os = detectPlatform();
    // Einmal bestimmen und durchreichen, damit Attribut und Context nicht
    // auseinanderlaufen koennen.
    const isNative = isCapacitorNativeSync();
    applyPlatformAttribute(os, isNative);
    return createPlatformState(os, isNative);
  }, []);

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
}

export function usePlatform(): PlatformState {
  const value = useContext(PlatformContext);
  if (!value) throw new Error('usePlatform must be used within PlatformProvider');
  return value;
}
